import { Fragment, useState } from 'react';
import Input from '../../../Abstracts/Components/Forms/Input';
import Checkbox from '../../../Abstracts/Components/Forms/Checkbox';
import styled from 'styled-components';
import { EftposPosList, EftposPosListItem } from 'shared-components';
import { Colors } from '../../../Abstracts/Style/Colors';
import LoadingOverlay from '../../../Abstracts/Components/LoadingOverlay';
import IconButton from '../../../Abstracts/Components/IconButton';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

export type EditableEftposPosListItem = Omit<
	EftposPosListItem,
	'ListId' | 'Created' | 'Modified' | 'EftposPosListItemPrice'
> & {
	EftposPosListItemPrice: string;
};

export type EditableEftposPosList = Omit<EftposPosList, 'Created' | 'Modified'> & {
	items: EditableEftposPosListItem[];
};

interface EditMenuFormProps {
	isFirstMenu: boolean;
	menu: EditableEftposPosList;
	setMenu: React.Dispatch<React.SetStateAction<EditableEftposPosList | undefined>>;
	onSave: (menu: EditableEftposPosList) => Promise<void>;
	saveButtonText: string;
}

export default function EditMenuForm(props: EditMenuFormProps) {
	const { isFirstMenu, menu, setMenu, onSave, saveButtonText } = props;
	const [loading, setLoading] = useState(false);

	const [validateActive, setValidateActive] = useState(false);

	const isValid =
		!validateActive ||
		(menu.EftposPosListName &&
			menu.EftposPosListName.length > 0 &&
			menu.items.every((item) => item.EftposPosListItemName && item.EftposPosListItemName.length > 0));

	return (
		<Container>
			{loading && <LoadingOverlay text="Saving menu" />}
			<div className="menu-name-container">
				{!isFirstMenu && (
					<>
						<Input
							className="menu-name"
							label="Menu name"
							placeholder="Menu name"
							value={menu.EftposPosListName ?? ''}
							onChange={(val) => setMenu({ ...menu, EftposPosListName: val })}
						/>
						{validateActive && (menu.EftposPosListName ?? '').length === 0 && (
							<div className="invalid-menu-name">A menu name is required</div>
						)}
					</>
				)}
			</div>
			<h4>Menu items</h4>
			<ItemsGrid>
				<div key="0-name" className="header name">
					Item name
				</div>
				<div key="0-price" className="header price">
					<Tippy delay={[0, 0]} content="All prices are GST inclusive.">
						<span>Price</span>
					</Tippy>
				</div>
				<div key="0-gst" className="header gst">
					<Tippy
						delay={[0, 0]}
						content="GST free items are items that are not subject to GST. Click the link below for more information."
					>
						<span>GST free item</span>
					</Tippy>
					<a
						href="https://www.ato.gov.au/business/gst/when-to-charge-gst-(and-when-not-to)/gst-free-sales/"
						target="_blank"
						rel="noopener noreferrer"
					>
						ATO guidelines
					</a>
				</div>
				<div key="0-delete" className="header" />
				{menu.items.map((item, index) => (
					<Fragment key={index}>
						<div key={`${index}-name`} className="name">
							<Input
								label="Item name"
								placeholder="Item name"
								value={item.EftposPosListItemName ?? ''}
								onChange={(val) => {
									const newItems = [...menu.items];
									const listItem = newItems[index];
									if (listItem) {
										listItem.EftposPosListItemName = val;
									}
									setMenu({ ...menu, items: newItems });
								}}
							/>
							<div className="validation-msg">
								{validateActive && item.EftposPosListItemName === '' && (
									<span className="error">Please enter an item name</span>
								)}
							</div>
						</div>
						<div key={`${index}-price`} className="price">
							<Input
								label="Item price"
								placeholder="Item price"
								value={item.EftposPosListItemPrice}
								type="number"
								onBlur={(e) => {
									const newItems = [...menu.items];
									const listItem = newItems[index];
									if (listItem) {
										if (isNaN(parseFloat(e.target.value))) {
											listItem.EftposPosListItemPrice = '0.00';
										} else {
											listItem.EftposPosListItemPrice = window.formatDecimal(e.target.value);
										}
									}
									setMenu({ ...menu, items: newItems });
								}}
								onChange={(val) => {
									const newItems = [...menu.items];
									const listItem = newItems[index];
									if (listItem) {
										listItem.EftposPosListItemPrice = val;
									}
									setMenu({ ...menu, items: newItems });
								}}
								inputProps={{
									min: 0,
								}}
							/>
						</div>
						<div key={`${index}-gst`} className="value gst">
							<Tippy
								delay={[0, 0]}
								content="Ticking this box indicates this item is exempt from GST in accordance with the ATO."
							>
								<Checkbox
									label="GST free item"
									value={item.EftposPosListItemGst === 0}
									onChange={(val) => {
										const newItems = [...menu.items];
										const listItem = newItems[index];
										if (listItem) {
											listItem.EftposPosListItemGst = val ? 0 : 1;
										}
										setMenu({ ...menu, items: newItems });
									}}
								/>
							</Tippy>
						</div>
						<div key={`${index}-delete`} className="delete">
							<IconButton
								onClick={() => {
									const newItems = [...menu.items];
									newItems.splice(index, 1);
									setMenu({ ...menu, items: newItems });
								}}
								className="btn btn-danger btn-sm"
								iconClass="fas fa-trash"
							>
								Remove
							</IconButton>
						</div>
					</Fragment>
				))}
				<div key="add" className="add">
					<button
						onClick={() => {
							const newItems = [...menu.items];
							newItems.push({
								EftposPosListItemId: 0,
								EftposPosListItemName: '',
								EftposPosListItemPrice: '0.00',
								EftposPosListItemGst: 1,
							});
							setMenu({ ...menu, items: newItems });
						}}
						className="btn btn-blue"
					>
						<i className="fas fa-plus" />
						Add new item
					</button>
				</div>
			</ItemsGrid>
			<button
				onClick={async () => {
					if (menu.EftposPosListName === '') {
						setValidateActive(true);
						return;
					}
					if (menu.items.length === 0) {
						// window.toast('Please add at least one item', 'error');
						return;
					}
					let hasEmpty = false;
					menu.items.forEach((item) => {
						if (item.EftposPosListItemName === '') {
							hasEmpty = true;
						}
					});
					if (hasEmpty) {
						setValidateActive(true);
						return;
					}
					setLoading(true);
					try {
						await onSave(menu);
					} catch (e) {
						setLoading(false);
						console.log(e);
					}
				}}
				className={`${!isValid ? 'disabled' : ''} btn btn-block btn-primary create-btn btn-lg`}
				disabled={!isValid}
			>
				{saveButtonText}
			</button>
		</Container>
	);
}

const ItemsGrid = styled.div`
	display: grid;
	grid-template-columns: auto 150px 150px 150px;

	background: ${Colors.gray1};

	> div {
		padding: 10px 10px;
	}

	.header {
		background: ${Colors.gray2};
		font-weight: 700;
		padding: 5px 10px;

		&.gst {
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
		}
	}

	.gst {
		display: flex;
		justify-content: center;
	}

	.value.gst {
		align-items: flex-start;
		padding-top: 20px;
	}

	.delete {
		text-align: center;
	}

	.add {
		grid-column: 1 / 5;

		button {
			display: flex;
			align-items: center;
			justify-content: center;
		}

		i {
			margin-right: 5px;
		}
	}

	.validation-msg {
		color: ${Colors.red};
		font-size: 12px;
		padding: 2px;
	}

	label {
		display: none;
	}

	@media (max-width: 768px) {
		.header {
			display: none;
		}

		display: flex;
		flex-wrap: wrap;

		.name {
			width: 70%;
		}

		.price {
			width: 30%;
		}

		.value.gst {
			width: 50%;
			padding-top: 14px;
			margin-bottom: 24px;
		}

		.delete {
			width: 50%;
		}

		label {
			display: block;
		}

		.add {
			width: 100%;

			button {
				width: 100%;
			}
		}
	}
`;

const Container = styled.div`
	display: flex;
	flex-direction: column;

	.menu-name {
		width: 200px;
	}

	.create-btn {
		margin-block: 50px;
	}

	.form-group {
		margin: 0;
	}

	.menu-name-container {
		.invalid-menu-name {
			color: ${Colors.red};
			font-size: 12px;
		}
		margin-bottom: 15px;
	}

	@media (max-width: 768px) {
		.menu-name {
			width: 100%;
		}
	}
`;
